import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { PWAManager } from './PWAManager';

interface PWAContextValue {
  pwaManager: PWAManager;
  isInstallable: boolean;
  isInstalled: boolean;
  canPromptInstall: boolean;
  promptInstall: () => Promise<boolean>;
  updateAvailable: boolean;
  applyUpdate: () => Promise<void>;
  shareContent: (shareData: ShareData) => Promise<boolean>;
  addToHomeScreen: () => void;
  enableFullscreen: () => void;
  exitFullscreen: () => void;
  hasUpdate: boolean;
  installPWA: () => Promise<boolean>;
  updatePWA: () => Promise<void>;
}

const PWAContext = createContext<PWAContextValue | null>(null);

interface PWAProviderProps {
  children: ReactNode;
  config?: {
    enableInstallPrompt?: boolean;
    enableNotifications?: boolean;
    enableOfflineMode?: boolean;
    installPromptDelay?: number;
    skipWaitingOnUpdate?: boolean;
  };
}

export const PWAProvider: React.FC<PWAProviderProps> = ({ 
  children, 
  config = {} 
}) => {
  const [pwaManager] = useState(() => new PWAManager(config));
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canPromptInstall, setCanPromptInstall] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Subscribe to install prompt availability
    const unsubscribeInstall = pwaManager.onInstallPromptReady((canInstall) => {
      setCanPromptInstall(canInstall);
    });

    // Subscribe to service worker updates
    const unsubscribeUpdate = pwaManager.onUpdateReady((registration) => {
      setUpdateAvailable(true);
      setPendingRegistration(registration);
    });

    // Update install status
    const updateInstallStatus = () => {
      const status = pwaManager.getInstallStatus();
      setIsInstallable(status.isInstallable);
      setIsInstalled(status.isInstalled);
      setCanPromptInstall(status.canPromptInstall);
    };

    updateInstallStatus();

    // Listen for app installation
    window.addEventListener('appinstalled', updateInstallStatus);

    return () => {
      unsubscribeInstall();
      unsubscribeUpdate();
      window.removeEventListener('appinstalled', updateInstallStatus);
    };
  }, [pwaManager]);

  const promptInstall = async (): Promise<boolean> => {
    const result = await pwaManager.promptInstall();
    if (result) {
      setCanPromptInstall(false);
      setIsInstalled(true);
    }
    return result;
  };

  const applyUpdate = async (): Promise<void> => {
    if (pendingRegistration) {
      await pwaManager.applyUpdate(pendingRegistration);
      setUpdateAvailable(false);
      setPendingRegistration(null);
    }
  };

  const shareContent = async (shareData: ShareData): Promise<boolean> => {
    return await pwaManager.shareContent(shareData);
  };

  const addToHomeScreen = (): void => {
    pwaManager.addToHomeScreen();
  };

  const enableFullscreen = (): void => {
    pwaManager.enableFullscreen();
  };

  const exitFullscreen = (): void => {
    pwaManager.exitFullscreen();
  };

  const contextValue: PWAContextValue = {
    pwaManager,
    isInstallable,
    isInstalled,
    canPromptInstall,
    promptInstall,
    updateAvailable,
    applyUpdate,
    shareContent,
    addToHomeScreen,
    enableFullscreen,
    exitFullscreen,
    hasUpdate: updateAvailable,
    installPWA: promptInstall,
    updatePWA: applyUpdate
  };

  return (
    <PWAContext.Provider value={contextValue}>
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = (): PWAContextValue => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};